﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ImageUploader } from "@/components/image-uploader";
import { LocationPicker } from "@/components/location-picker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/**
 * Create/edit event form. Backend event slug'ı Title'dan üretir; create'te kullanıcı override edebilir.
 * Tarihler datetime-local formatında alınır, submit'te ISO string olarak gönderilir.
 * TicketType ekleme/silme ayrı komponentte — bu form sadece core event meta'sını yönetir.
 */
const baseSchema = {
  title: z.string().min(2, "Başlık en az 2 karakter.").max(200),
  description: z.string().max(10000).optional().or(z.literal("")),
  coverImageUrl: z.string().url("Geçerli URL gir.").optional().or(z.literal("")),
  venueName: z.string().max(200).optional().or(z.literal("")),
  venueAddress: z.string().max(500).optional().or(z.literal("")),
  venueCity: z.string().max(100).optional().or(z.literal("")),
  // Harita picker set eder. Manuel girilemez (LocationPicker readonly gösterir).
  latitude: z
    .number()
    .min(-90)
    .max(90)
    .nullable()
    .optional(),
  longitude: z
    .number()
    .min(-180)
    .max(180)
    .nullable()
    .optional(),
  startsAt: z.string().min(1, "Başlangıç zamanı zorunlu."),
  endsAt: z.string().min(1, "Bitiş zamanı zorunlu."),
  defaultTaxRate: z.coerce
    .number()
    .min(0, "KDV 0% altında olamaz.")
    .max(100, "KDV 100%'ü geçemez."),
};

const createSchema = z.object({
  ...baseSchema,
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Sadece küçük harf, rakam ve tire.")
    .min(3)
    .max(80)
    .optional()
    .or(z.literal("")),
});

const editSchema = z.object(baseSchema);

export type CreateEventFormValues = z.infer<typeof createSchema>;
export type EditEventFormValues = z.infer<typeof editSchema>;
type CreateEventFormInput = z.input<typeof createSchema>;
type EditEventFormInput = z.input<typeof editSchema>;

type EventFormProps =
  | {
      mode: "create";
      defaultValues?: Partial<CreateEventFormValues>;
      onSubmit: (values: CreateEventFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    }
  | {
      mode: "edit";
      /** Edit modda zorunlu — ImageUploader bu id ile S3 key'i türetir ve ownership check yapar. */
      eventId: string;
      defaultValues?: Partial<EditEventFormValues>;
      onSubmit: (values: EditEventFormValues) => Promise<void> | void;
      isSubmitting?: boolean;
      submitLabel?: string;
    };

export function EventForm(props: EventFormProps) {
  if (props.mode === "create") {
    return <CreateFormImpl {...props} />;
  }
  return <EditFormImpl {...props} />;
}

type CreateFormProps = Extract<EventFormProps, { mode: "create" }>;

function CreateFormImpl({ defaultValues, onSubmit, isSubmitting, submitLabel }: CreateFormProps) {
  const form = useForm<CreateEventFormInput, unknown, CreateEventFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      title: "",
      description: "",
      coverImageUrl: "",
      slug: "",
      venueName: "",
      venueAddress: "",
      venueCity: "",
      latitude: null,
      longitude: null,
      startsAt: "",
      endsAt: "",
      defaultTaxRate: 20,
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <CoreFields form={form} />

        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL Slug</FormLabel>
              <FormControl>
                <Input placeholder="boş bırak → otomatik üretilir" {...field} />
              </FormControl>
              <FormDescription>
                Etkinlik URL&apos;i: <span className="font-mono">/events/{field.value || "auto"}</span>
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <VenueFields form={form} />
        <DateFields form={form} />

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? "Taslak Oluştur"} />
      </form>
    </Form>
  );
}

type EditFormProps = Extract<EventFormProps, { mode: "edit" }>;

function EditFormImpl({ eventId, defaultValues, onSubmit, isSubmitting, submitLabel }: EditFormProps) {
  const form = useForm<EditEventFormInput, unknown, EditEventFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      title: "",
      description: "",
      coverImageUrl: "",
      venueName: "",
      venueAddress: "",
      venueCity: "",
      latitude: null,
      longitude: null,
      startsAt: "",
      endsAt: "",
      defaultTaxRate: 20,
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <CoreFields form={form} eventId={eventId} />
        <VenueFields form={form} />
        <DateFields form={form} />

        <SubmitRow isSubmitting={isSubmitting} label={submitLabel ?? "Değişiklikleri Kaydet"} />
      </form>
    </Form>
  );
}

// ---- Alt bölümler ----

type AnyForm =
  | UseFormReturn<CreateEventFormInput, unknown, CreateEventFormValues>
  | UseFormReturn<EditEventFormInput, unknown, EditEventFormValues>;

function CoreFields({ form, eventId }: { form: AnyForm; eventId?: string }) {
  return (
    <div className="space-y-5">
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Etkinlik Adı *</FormLabel>
            <FormControl>
              <Input placeholder="Örn: Yaz Konseri 2026" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Açıklama</FormLabel>
            <FormControl>
              <Textarea
                rows={5}
                placeholder="Etkinlik hakkında detay... (Markdown destekli)"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="coverImageUrl"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Kapak Görseli</FormLabel>
            <FormControl>
              {eventId ? (
                <ImageUploader
                  kind="EventCover"
                  eventId={eventId}
                  aspect="cover"
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={(url) => field.onChange(url ?? "")}
                />
              ) : (
                <Input placeholder="https://..." {...field} />
              )}
            </FormControl>
            {!eventId ? (
              <FormDescription>
                Taslak oluşturduktan sonra buradan görsel yükleyebilirsin.
              </FormDescription>
            ) : null}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function VenueFields({ form }: { form: AnyForm }) {
  // `AnyForm` iki form varyantının union'u; TS union narrowing setValue/watch
  // imzalarını intersect edemediği için locale cast ediyoruz. Her iki varyant
  // da aynı venue alanlarını içeriyor → cast güvenli.
  const f = form as unknown as UseFormReturn<
    EditEventFormInput,
    unknown,
    EditEventFormValues
  >;
  const latitude = f.watch("latitude") ?? null;
  const longitude = f.watch("longitude") ?? null;

  // LocationPicker seçim yapınca: lat/lon + adres + şehir otomatik güncellenir.
  // Eski Biletzero projesindeki davranışla hizalı — haritadan seçim tüm alanları
  // override eder. Kullanıcı manuel yazmak isterse haritada başka nokta seçmez.
  const handleLocationChange = (
    coords: {
      latitude: number;
      longitude: number;
      address?: string | null;
      city?: string | null;
    } | null,
  ) => {
    if (coords) {
      f.setValue("latitude", coords.latitude, { shouldDirty: true });
      f.setValue("longitude", coords.longitude, { shouldDirty: true });
      if (coords.address) {
        f.setValue("venueAddress", coords.address, { shouldDirty: true });
      }
      // Şehir sadece boşsa doldur — kullanıcı özellikle "Kadıköy" yazdıysa
      // Nominatim'in "İstanbul" dönüşü bunu ezmesin.
      const currentCity = f.getValues("venueCity") ?? "";
      if (!currentCity && coords.city) {
        f.setValue("venueCity", coords.city, { shouldDirty: true });
      }
    } else {
      f.setValue("latitude", null, { shouldDirty: true });
      f.setValue("longitude", null, { shouldDirty: true });
    }
  };

  return (
    <div className="space-y-5 rounded-lg border p-4">
      <h3 className="text-sm font-semibold">Mekan</h3>
      <p className="text-xs text-muted-foreground">
        Yayına almak için en az mekan adı zorunlu.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="venueName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mekan Adı</FormLabel>
              <FormControl>
                <Input placeholder="Örn: Zorlu PSM" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="venueCity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Şehir</FormLabel>
              <FormControl>
                <Input placeholder="İstanbul" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="venueAddress"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Adres</FormLabel>
            <FormControl>
              <Input placeholder="Mahalle, sokak, kapı no..." {...field} />
            </FormControl>
            <FormDescription>
              Haritadan nokta seçince otomatik doldurulur (boşsa).
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormItem>
        <FormLabel>Konum</FormLabel>
        <LocationPicker
          latitude={typeof latitude === "number" ? latitude : null}
          longitude={typeof longitude === "number" ? longitude : null}
          onChange={handleLocationChange}
        />
      </FormItem>
    </div>
  );
}

function DateFields({ form }: { form: AnyForm }) {
  return (
    <div className="space-y-5 rounded-lg border p-4">
      <h3 className="text-sm font-semibold">Tarihler</h3>
      <p className="text-xs text-muted-foreground">
        Yerel saatini gir. Sistem UTC olarak saklar, göstermek için otomatik çevirir.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="startsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Başlangıç *</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endsAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bitiş *</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="defaultTaxRate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Default KDV Oranı (%)</FormLabel>
            <FormControl>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="0.5"
                {...field}
                value={
                  typeof field.value === "number" || typeof field.value === "string"
                    ? field.value
                    : 20
                }
              />
            </FormControl>
            <FormDescription>
              Bu etkinliğin bilet türlerine uygulanacak varsayılan KDV. Bilet bazlı
              özel oran, bilet tanımında belirlenir.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function SubmitRow({ isSubmitting, label }: { isSubmitting?: boolean; label: string }) {
  return (
    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {isSubmitting ? "Kaydediliyor..." : label}
    </Button>
  );
}
